import { Navigate, useRoutes, useLocation, useParams, Outlet } from "react-router-dom";
import { Suspense, lazy } from "react";
import { PATH_DASHBOARD } from "./paths";
// layouts
import DashboardLayout from "../layouts/dashboard";
import LogoOnlyLayout from "../layouts/LogoOnlyLayout";
// guards
import GuestGuard from "../guards/GuestGuard";
import AuthGuard from "../guards/AuthGuard";
// import RoleBasedGuard from '../guards/RoleBasedGuard';
// components
import LoadingScreen from "../components/LoadingScreen";
import NewUserGuard from "guards/NewUserGuard";
import PaidCustomerGuard from "guards/PaidCustomerGuard";
import FeatureGate from "guards/FeatureGate";
import VirtualNumberGate from "guards/VirtualNumberGate";
import useFirestore from "hooks/useFirestore";
import { Box } from "@material-ui/system";
import { LinearProgress } from "@material-ui/core";

// ----------------------------------------------------------------------

const Loadable = (Component: React.ElementType) => (props: any) => {
  // eslint-disable-next-line react-hooks/rules-of-hooks
  const { pathname } = useLocation();
  const isDashboard = pathname.includes("/dashboard");

  return (
    <Suspense
      fallback={
        <LoadingScreen
          sx={{
            ...(!isDashboard && {
              top: 0,
              left: 0,
              width: 1,
              zIndex: 9999,
              position: "fixed"
            })
          }}
        />
      }
    >
      <Component {...props} />
    </Suspense>
  );
};

/**
 * `/dashboard/crm/audiences/:audienceId` → `/dashboard/whatsapp/audiences/:audienceId`. A plain
 * `<Navigate/>` cannot carry a param across, and the id is phone-shaped in places (may hold
 * '+'), so it is re-encoded through the same helper every in-app link uses.
 */
function LegacyAudienceDetailRedirect(): React.ReactElement {
  const { audienceId = "" } = useParams<{ audienceId: string }>();
  return <Navigate to={PATH_DASHBOARD.general.whatsappAudienceDetail(audienceId)} replace />;
}

function renderDashboardMainPage(isLoadingUser: boolean, isAdmin: boolean): React.ReactElement {
  if (isLoadingUser) {
    return (
      <Box my={4} p={2} sx={{ width: "100%" }}>
        <LinearProgress />
      </Box>
    );
  }

  if (isAdmin) {
    return <AdminPage />;
  }

  return <DashboardMain />;
}

export default function Router() {
  const { isLoadingUser, isAdminOfTheOrganization } = useFirestore();

  return useRoutes([
    {
      path: "auth",
      children: [
        {
          path: "login",
          element: (
            <GuestGuard>
              <Login />
            </GuestGuard>
          )
        },
        {
          path: "register",
          element: (
            <GuestGuard>
              <Register />
            </GuestGuard>
          )
        },
        { path: "login-unprotected", element: <Login /> },
        { path: "register-unprotected", element: <Register /> },
        { path: "reset-password", element: <ResetPassword /> },
        { path: "verify", element: <VerifyCode /> }
      ]
    },
    // Quick route for bot builder
    {
      path: "nadlan",
      children: [
        {
          path: "",
          element: <Navigate to="/dashboard/autoreplyBotSettings/bot-autoreply" replace />
        }
      ]
    },
    // A buyer's minisite in its realtor's editor: signed in, outside the dashboard's frame (the page is the
    // minisite itself, with the owner bar on top). The buyer's own page is the client portal's
    // (/myListings/**, served by the server); its owner strip leads here.
    {
      path: "dashboard/minisite",
      element: (
        <AuthGuard>
          <Outlet />
        </AuthGuard>
      ),
      children: [
        { path: ":listingFavoritesId", element: <ListingsMatchClientPublicPage editor /> },
        { path: ":listingFavoritesId/:listingId", element: <ListingsMatchClientPublicPage editor /> }
      ]
    },
    // The sales portal for reps (2026-10-28): signed in, and NOTHING else. A sales rep is not a customer: under
    // "dashboard" the onboarding (NewUserGuard) and the paid-plan gate (PaidCustomerGuard) stopped every rep before the
    // page loaded. Who may read it is the server's answer (the owners + the readers they add); the page shows a
    // "no access yet" card to anyone else. Owners also reach it inside the dashboard (/dashboard/admin/sales-training-v2).
    {
      path: "sales-portal",
      element: (
        <AuthGuard>
          <AdminSalesTrainingV2Page standalone />
        </AuthGuard>
      )
    },
    // Dashboard Routes
    {
      path: "dashboard",
      element: (
        <AuthGuard>
          <NewUserGuard>
            <PaidCustomerGuard>
              <DashboardLayout />
            </PaidCustomerGuard>
          </NewUserGuard>
        </AuthGuard>
      ),
      children: [
        { path: "", element: <Navigate to="/dashboard/main" replace /> },
        { path: "main", element: renderDashboardMainPage(isLoadingUser, isAdminOfTheOrganization) },
        // The legacy AG Grid of the whole book lived here. Nothing in the app linked to it any
        // more; the path stays as a redirect for the bookmark someone still has.
        { path: "users", element: <Navigate to={PATH_DASHBOARD.general.contacts} replace /> },
        {
          path: "contacts",
          element: (
            <FeatureGate feature="crm">
              <ContactsPage />
            </FeatureGate>
          )
        },
        {
          // The Postgres-backed CRM surface. Same guard stack as /dashboard/contacts;
          // that page stays mounted at its own route so rollback is a link.
          path: "crm",
          element: (
            <FeatureGate feature="crm">
              <CrmPage />
            </FeatureGate>
          )
        },
        {
          // Agency settings — members, roles, contact-visibility strategy.
          //
          // Now a settings SECTION (`orgPermissions`), so its canonical home is the
          // settings page and its in-app entry point is a `SettingsPanel` from the CRM
          // header. This path stays as a redirect because it is what existed before and
          // is what any saved link or old message points at; the gate moves with it —
          // `/dashboard/settings/orgPermissions` renders the section only when the
          // registry's own `crm` + org check passes.
          path: "crm/settings",
          element: <Navigate to="/dashboard/settings/orgPermissions" replace />
        },
        {
          // Audiences (קהלים) and the blocked list (חסומים לדיוור) moved from the contacts area
          // to tabs of the WhatsApp hub. These paths stay as redirects because they are what
          // any saved link, snackbar or old message points at; the gate lives on the target.
          path: "crm/audiences",
          element: <Navigate to={PATH_DASHBOARD.general.whatsappAudiences} replace />
        },
        {
          path: "crm/audiences/:audienceId",
          element: <LegacyAudienceDetailRedirect />
        },
        {
          path: "crm/blocked",
          element: <Navigate to={PATH_DASHBOARD.general.whatsappBlocked} replace />
        },
        {
          // טופס לידים's leads page, for a tab still open on it: the form writes its people into
          // the contacts now (Haim, 2026-10-19), and its links open from the CRM page's button.
          path: "crm/leads",
          element: <Navigate to={PATH_DASHBOARD.general.crm} replace />
        },
        {
          // Full-page customer view (עמוד לקוח). Param = contact Firestore doc id.
          // Same guard stack as /dashboard/contacts (dashboard guards + crm gate).
          path: "contacts/:contactId",
          element: (
            <FeatureGate feature="crm">
              <ContactDetailPage />
            </FeatureGate>
          )
        },
        {
          // The old calls screen. Nothing in the app links here any more, but every WhatsApp
          // call summary sent before the server switched to `newCalls` does, so the path stays
          // as a redirect; the gate lives on the target.
          path: "calls",
          element: <Navigate to={PATH_DASHBOARD.general.newCalls} replace />
        },
        {
          path: "newCalls",
          element: (
            <VirtualNumberGate>
              <CallsPage />
            </VirtualNumberGate>
          )
        },
        {
          path: "leads",
          children: [
            {
              // The import hub — real routes since D6 (`/import`, `/import/whatsapp`,
              // `/import/file`, `/import/mobile`); the page reads `:importSource`.
              path: "import",
              element: (
                <FeatureGate feature="crm">
                  <ImportLeadsPage />
                </FeatureGate>
              )
            },
            {
              path: "import/:importSource",
              element: (
                <FeatureGate feature="crm">
                  <ImportLeadsPage />
                </FeatureGate>
              )
            },
            {
              // טופס לידים's first address, for a tab still open on it: the form lives in the CRM.
              path: "intake",
              element: <Navigate to={PATH_DASHBOARD.general.crm} replace />
            }
          ]
        },
        {
          path: "calendar",
          element: (
            <FeatureGate feature="calendar">
              <CalendarPage />
            </FeatureGate>
          )
        },
        {
          path: "listings",
          element: (
            <FeatureGate feature="listings">
              <ListingsPage />
            </FeatureGate>
          )
        },
        {
          path: "listings/:listingId",
          element: <OpenListingTriggerPage />
        },
        {
          // A WhatsApp listing card's button: the listings of that one card.
          path: "listing-alerts/:cardId",
          element: <ListingAlertCardPage />
        },
        {
          path: "listingsMatch",
          element: (
            <FeatureGate feature="listingsMatch">
              <ListingsMatchPage />
            </FeatureGate>
          )
        },
        {
          path: "rentalToSale",
          element: (
            <FeatureGate feature="listingsMatch">
              <RentalToSalePage />
            </FeatureGate>
          )
        },
        {
          path: "listings-board",
          element: (
            <FeatureGate feature="listingsMatch">
              <ListingsBoardPage />
            </FeatureGate>
          )
        },
        {
          path: "notifications",
          element: (
            <FeatureGate feature="listingsMatch">
              <NotificationsPage />
            </FeatureGate>
          )
        },
        {
          path: "editContactBeforeListingsMatch",
          element: (
            <FeatureGate feature="listingsMatch">
              <EditContactBeforeListingsMatchPage />
            </FeatureGate>
          )
        },
        { path: "creatives-generator", element: <CreativesGeneratorPage /> },
        { path: "makeovers", element: <MakeoversPage /> },
        {
          path: "facebook-leads",
          element: (
            <FeatureGate feature="facebookLeads">
              <FacebookLeadsPage />
            </FeatureGate>
          )
        },
        {
          path: "fb-posting",
          element: (
            <FeatureGate feature="facebookGroupPosting">
              <FbPostingPage />
            </FeatureGate>
          )
        },
        {
          // The WhatsApp hub — one sidebar entry, top tabs (חיבור/הודעות/תבניות/קמפיינים/
          // קהלים/חסומים לדיוור). The official URLs are unchanged: the old flat routes became
          // children, so bookmarks keep working.
          //
          // Gating is two-level on purpose. The audiences and blocked tabs serve BOTH worlds —
          // a crm-only agency builds audiences and blocks numbers for the extension sender, an
          // officialWhatsapp-only one sends to them — so the LAYOUT admits any of the three
          // features and each official child re-gates on `officialWhatsapp` itself. Collapsing
          // this to one gate either locks crm-only customers out of their blocked list or lets
          // them into the connection/campaign screens. The layout hides the tabs the viewer
          // cannot enter.
          path: "whatsapp",
          element: (
            <FeatureGate anyOf={["officialWhatsapp", "crm", "bulkMessaging"]}>
              <WhatsAppHubLayout />
            </FeatureGate>
          ),
          children: [
            {
              path: "",
              element: (
                <FeatureGate feature="officialWhatsapp">
                  <WhatsAppOfficialPage />
                </FeatureGate>
              )
            },
            {
              path: "messages",
              element: (
                <FeatureGate feature="officialWhatsapp">
                  <WhatsAppMessagesPage />
                </FeatureGate>
              )
            },
            {
              path: "templates",
              element: (
                <FeatureGate feature="officialWhatsapp">
                  <WhatsAppTemplatesPage />
                </FeatureGate>
              )
            },
            {
              // The smart assistant's settings + test chat. Gated on `officialWhatsapp` like its
              // siblings; the finer gate is the TAB, which renders only for a number that has an
              // assistant config — so a customer who types this URL on a number without one gets
              // the page's own "no assistant here" state rather than a 404.
              path: "assistant",
              element: (
                <FeatureGate feature="officialWhatsapp">
                  <WhatsAppAssistantPage />
                </FeatureGate>
              )
            },
            // Old "קבוצות תפוצה" bookmarks land on the audiences tab.
            { path: "groups", element: <Navigate to={PATH_DASHBOARD.general.whatsappAudiences} replace /> },
            {
              path: "campaigns",
              element: (
                <FeatureGate feature="officialWhatsapp">
                  <WhatsAppCampaignsPage />
                </FeatureGate>
              )
            },
            {
              // Full-page campaign wizard. `?templateDocId=` / `?audienceId=` are the hand-offs
              // from the templates tab and the CRM bulk bar. Declared before `:campaignId`.
              path: "campaigns/new",
              element: (
                <FeatureGate feature="officialWhatsapp">
                  <WhatsAppCampaignWizardPage />
                </FeatureGate>
              )
            },
            {
              // The sent-messages log ("view all" from the campaigns tab). Declared before `:campaignId`.
              path: "campaigns/log",
              element: (
                <FeatureGate feature="officialWhatsapp">
                  <WhatsAppSentMessagesLogPage />
                </FeatureGate>
              )
            },
            {
              // Routed control room (replaces the old detail dialog). `?preview=1` = create handoff.
              path: "campaigns/:campaignId",
              element: (
                <FeatureGate feature="officialWhatsapp">
                  <WhatsAppCampaignDetailPage />
                </FeatureGate>
              )
            },
            {
              // Audiences (קהלים) — recipient lists. OR-gated (see the layout comment above).
              path: "audiences",
              element: (
                <FeatureGate anyOf={["crm", "officialWhatsapp", "bulkMessaging"]}>
                  <AudiencesPage />
                </FeatureGate>
              )
            },
            {
              path: "audiences/:audienceId",
              element: (
                <FeatureGate anyOf={["crm", "officialWhatsapp", "bulkMessaging"]}>
                  <AudienceDetailPage />
                </FeatureGate>
              )
            },
            {
              // The blocked-numbers list (חסומים לדיוור). OR-gated: it governs BOTH worlds —
              // official campaigns and the extension bulk sender — so either feature reaches it.
              path: "blocked",
              element: (
                <FeatureGate anyOf={["crm", "officialWhatsapp", "bulkMessaging"]}>
                  <BlockedNumbersPage />
                </FeatureGate>
              )
            }
          ]
        },
        {
          path: "ai",
          element: (
            <FeatureGate feature="ai">
              <ChatPage />
            </FeatureGate>
          )
        },
        {
          path: "ai/:chatId",
          element: (
            <FeatureGate feature="ai">
              <ChatPage />
            </FeatureGate>
          )
        },
        {
          path: "links",
          element: (
            <FeatureGate feature="listingsMatch">
              <LinksControlPanelPage />
            </FeatureGate>
          )
        },
        { path: "training", element: <TrainingPage /> },
        { path: "admin/creatives-generator", element: <AdminCreativesGeneratorPage /> },
        { path: "admin/training", element: <AdminTrainingPage /> },
        { path: "admin/ai-review", element: <AdminAiReviewPage /> },
        { path: "admin/ai-review/:userId", element: <AdminAiReviewPage /> },
        { path: "admin/ai-review/:userId/:chatId", element: <AdminAiReviewPage /> },
        { path: "admin/feedbacks", element: <AdminFeedbacksPage /> },
        { path: "admin/coins", element: <AdminCoinsManagementPage /> },
        { path: "admin/signatures", element: <AdminSignaturesPage /> },
        { path: "admin/locations", element: <AdminLocationsPage /> },
        { path: "admin/facebook-sources-stats", element: <AdminFacebookSourcesStatsPage /> },
        { path: "admin/users", element: <AdminUsersManagementPage /> },
        { path: "admin/users/:userId", element: <AdminUsersManagementPage /> },
        { path: "admin/db", element: <AdminDbExplorerPage /> },
        { path: "admin/db/:dbKey", element: <AdminDbExplorerPage /> },
        { path: "admin/db/:dbKey/:tableName", element: <AdminDbExplorerPage /> },
        { path: "admin/sales-map", element: <AdminSalesMapPage /> },
        { path: "admin/sales-training", element: <AdminSalesTrainingPage /> },
        { path: "admin/sales-training-v2", element: <AdminSalesTrainingV2Page /> },
        { path: "admin/recruiting", element: <AdminSalesRecruitingPage /> },
        { path: "settings", element: <SettingPage /> },
        // Every settings section is addressable, which is what lets a release-note CTA,
        // a banner or a `SettingsPanel`'s "open in full settings" link land on the right
        // one. An unknown or gated-away id renders the index rather than 404ing — see
        // `SettingPage`.
        { path: "settings/:sectionId", element: <SettingPage /> },
        { path: "unanswered-calls-bot-responses", element: <InDevelopmentPage /> },
        {
          path: "whatsAppWeb",
          children: [
            { path: "status", element: <WhatsAppExtensionTutorialPage /> },
            {
              path: "sendMessages",
              element: (
                <FeatureGate feature="bulkMessaging">
                  <WhatsAppWebSendMessagesPage />
                </FeatureGate>
              )
            }
          ]
        },
        {
          path: "autoreplyBotSettings",
          children: [
            {
              path: "bot-autoreply",
              element: (
                <FeatureGate feature="simpleQuestionResponseBot">
                  <BotAutoReplyPage />
                </FeatureGate>
              )
            }
          ]
        },
        {
          path: "signatures",
          element: (
            <FeatureGate feature="signatures">
              <Outlet />
            </FeatureGate>
          ),
          children: [
            { path: "", element: <SignaturesHub /> },
            { path: "sent", element: <SignaturesDocumentsList /> },
            { path: "new", element: <SignaturesNewDocument /> },
            { path: "templates", element: <SignaturesTemplatesList /> },
            { path: "marketplace", element: <SignaturesMarketplace /> },
            { path: "templates/:templateId", element: <SignaturesTemplateEditor /> },
            { path: ":documentId", element: <SignaturesDocumentDetail /> }
          ]
        }
      ]
    },
    // The realtor's public page and its listing pages moved to the client portal (/r/<alias>,
    // /r/<alias>/l/<id>; served by Hosting and the server, not this app). Hosting 301s the old
    // /listings/u/<alias> and /listings/<alias>/<id> addresses there (firebase.json), so they never
    // reach this router; utils/realtorSite/realtorSiteUrls.ts builds the new ones.
    {
      path: "listings",
      children: [{ path: "", element: <Navigate to="/dashboard/main" replace /> }]
    },
    // A buyer's minisite is the client portal's page: Hosting sends /myListings/** to the server, so a link
    // never reaches this app. The old page stays here as the way back: remove that rewrite from
    // firebase.json and every minisite works as it did before 2026-10-26. Its realtor edits it at
    // /dashboard/minisite (above).
    {
      path: "myListings",
      children: [
        { path: ":listingFavoritesId", element: <ListingsMatchClientPublicPage /> },
        { path: ":listingFavoritesId/:listingId", element: <ListingsMatchClientPublicPage /> }
      ]
    },
    // Public Signature Route - No Auth Required
    {
      path: "sign",
      children: [{ path: ":submissionId", element: <StandaloneSignPage /> }]
    },
    // Public completed-document viewer (branded short link `/d/:documentId`
    // used in completion SMS + dashboard). No auth — id-gated, completed docs only.
    {
      path: "d",
      children: [{ path: ":documentId", element: <StandaloneDocumentViewer /> }]
    },
    // Main Routes
    {
      path: "*",
      element: <LogoOnlyLayout />,
      children: [
        { path: "maintenance", element: <Maintenance /> },
        { path: "500", element: <Page500 /> },
        { path: "404", element: <NotFound /> },
        // Replacing the default 404 redirect with a custom one
        // { path: '*', element: <Navigate to="/404" replace /> }
        { path: "*", element: <BotShortLinksRedirectPage /> }
      ]
    },
    {
      path: "",
      element: <Navigate to="/dashboard" replace />
    },
    // Replacing the default 404 redirect with a custom one
    // { path: '*', element: <Navigate to="/404" replace /> }
    { path: "*", element: <BotShortLinksRedirectPage /> }
  ]);
}

// IMPORT COMPONENTS

// Authentication
const Login = Loadable(lazy(() => import("../pages/authentication/Login")));
const Register = Loadable(lazy(() => import("../pages/authentication/Register")));
const ResetPassword = Loadable(lazy(() => import("../pages/authentication/ResetPassword")));
const VerifyCode = Loadable(lazy(() => import("../pages/authentication/VerifyCode")));
// Dashboard
const AdminPage = Loadable(lazy(() => import("../pages/dashboard/AdminHome")));
const ContactsPage = Loadable(lazy(() => import("../pages/dashboard/ContactsPage")));
const ListingAlertCardPage = Loadable(lazy(() => import("../pages/dashboard/ListingAlertCardPage")));
const CrmPage = Loadable(lazy(() => import("../pages/dashboard/CrmPage")));
const BlockedNumbersPage = Loadable(lazy(() => import("../pages/dashboard/BlockedNumbersPage")));
const AudiencesPage = Loadable(lazy(() => import("../pages/dashboard/AudiencesPage")));
const AudienceDetailPage = Loadable(lazy(() => import("../pages/dashboard/AudienceDetailPage")));
const ContactDetailPage = Loadable(lazy(() => import("../pages/dashboard/ContactDetailPage")));
const DashboardMain = Loadable(lazy(() => import("../pages/DashboardMain")));
const CallsPage = Loadable(lazy(() => import("../pages/dashboard/CallsPage")));
const ImportLeadsPage = Loadable(lazy(() => import("../pages/dashboard/ImportLeadsPage")));
const WhatsAppExtensionTutorialPage = Loadable(
  lazy(() => import("../pages/dashboard/WhatsAppExtensionTutorialPage"))
);
const WhatsAppWebSendMessagesPage = Loadable(
  lazy(() => import("../pages/dashboard/WhatsAppWebSendMessagesPage"))
);
const BotShortLinksRedirectPage = Loadable(
  lazy(() => import("../pages/dashboard/BotShortLinksRedirectPage"))
);
const BotAutoReplyPage = Loadable(lazy(() => import("../pages/dashboard/BotAutoReplyPage")));
const ListingsPage = Loadable(lazy(() => import("../pages/dashboard/ListingsPage")));
const ListingsMatchPage = Loadable(lazy(() => import("../pages/dashboard/ListingsMatchPage")));
const RentalToSalePage = Loadable(lazy(() => import("../pages/dashboard/RentalToSalePage")));
const ListingsBoardPage = Loadable(lazy(() => import("../pages/dashboard/ListingsBoardPage")));
const NotificationsPage = Loadable(lazy(() => import("../pages/dashboard/NotificationsPage")));
const OpenListingTriggerPage = Loadable(
  lazy(() => import("../pages/dashboard/OpenListingTriggerPage"))
);
const EditContactBeforeListingsMatchPage = Loadable(
  lazy(() => import("../pages/dashboard/EditContactBeforeListingsMatchPage"))
);
const CreativesGeneratorPage = Loadable(
  lazy(() => import("../pages/dashboard/CreativesGeneratorPage"))
);
const MakeoversPage = Loadable(lazy(() => import("../pages/dashboard/MakeoversPage")));
const ChatPage = Loadable(lazy(() => import("../pages/dashboard/ChatPage")));
const FacebookLeadsPage = Loadable(lazy(() => import("../pages/dashboard/FacebookLeadsPage")));
const FbPostingPage = Loadable(lazy(() => import("../pages/dashboard/FbPostingPage")));
// The WhatsApp hub's frame and its landing tab are PREFETCHED (fetched at idle once the app has
// loaded). Lazily, they were two ~0.45s downloads one after the other on a CDN miss — the layout's,
// then the page's it renders — and every deploy is a miss. ~14KB gzipped for both.
const WhatsAppOfficialPage = Loadable(
  lazy(() => import(/* webpackPrefetch: true */ "../pages/dashboard/WhatsAppOfficialPage"))
);
const WhatsAppMessagesPage = Loadable(lazy(() => import("../pages/dashboard/WhatsAppMessagesPage")));
const WhatsAppTemplatesPage = Loadable(lazy(() => import("../pages/dashboard/WhatsAppTemplatesPage")));
const WhatsAppCampaignsPage = Loadable(lazy(() => import("../pages/dashboard/WhatsAppCampaignsPage")));
const WhatsAppHubLayout = Loadable(
  lazy(() => import(/* webpackPrefetch: true */ "../pages/dashboard/whatsapp/WhatsAppHubLayout"))
);
const WhatsAppAssistantPage = Loadable(lazy(() => import("../pages/dashboard/whatsapp/WhatsAppAssistantPage")));
const WhatsAppCampaignDetailPage = Loadable(lazy(() => import("../pages/dashboard/whatsapp/CampaignDetailPage")));
const WhatsAppCampaignWizardPage = Loadable(lazy(() => import("../pages/dashboard/whatsapp/CampaignWizardPage")));
const WhatsAppSentMessagesLogPage = Loadable(lazy(() => import("../pages/dashboard/whatsapp/SentMessagesLogPage")));
const AdminCreativesGeneratorPage = Loadable(
  lazy(() => import("../pages/dashboard/admin/AdminCreativesGeneratorPage"))
);
const AdminCoinsManagementPage = Loadable(
  lazy(() => import("../pages/dashboard/admin/AdminCoinsManagementPage"))
);
const AdminSignaturesPage = Loadable(
  lazy(() => import("../pages/dashboard/admin/AdminSignaturesPage"))
);
const AdminLocationsPage = Loadable(
  lazy(() => import("../pages/dashboard/admin/AdminLocationsPage"))
);
const AdminFacebookSourcesStatsPage = Loadable(
  lazy(() => import("../pages/dashboard/admin/AdminFacebookSourcesStatsPage"))
);
const AdminUsersManagementPage = Loadable(
  lazy(() => import("../pages/dashboard/admin/AdminUsersManagementPage"))
);
const AdminDbExplorerPage = Loadable(
  lazy(() => import("../pages/dashboard/admin/AdminDbExplorerPage"))
);
const AdminSalesMapPage = Loadable(
  lazy(() => import("../pages/dashboard/admin/AdminSalesMapPage"))
);
const AdminSalesTrainingPage = Loadable(
  lazy(() => import("../pages/dashboard/admin/AdminSalesTrainingPage"))
);
const AdminSalesTrainingV2Page = Loadable(
  lazy(() => import("../pages/dashboard/admin/AdminSalesTrainingV2Page"))
);
const AdminSalesRecruitingPage = Loadable(
  lazy(() => import("../pages/dashboard/admin/AdminSalesRecruitingPage"))
);
const TrainingPage = Loadable(lazy(() => import("../pages/dashboard/TrainingPage")));
const LinksControlPanelPage = Loadable(
  lazy(() => import("../pages/dashboard/LinksControlPanelPage"))
);
const AdminTrainingPage = Loadable(
  lazy(() => import("../pages/dashboard/admin/AdminTrainingPage"))
);
const AdminAiReviewPage = Loadable(
  lazy(() => import("../pages/dashboard/admin/AdminAiReviewPage"))
);
const AdminFeedbacksPage = Loadable(
  lazy(() => import("../pages/dashboard/admin/AdminFeedbacksPage"))
);
const ListingsMatchClientPublicPage = Loadable(
  lazy(() => import("../pages/dashboard/ListingsMatchClientPublicPage"))
);
// Signatures (new Firestore-first feature; see .claude/plans/tidy-yawning-hippo.md)
const SignaturesHub = Loadable(lazy(() => import("../pages/dashboard/signatures/SignaturesHub")));
const SignaturesDocumentsList = Loadable(
  lazy(() => import("../pages/dashboard/signatures/DocumentsList"))
);
const SignaturesNewDocument = Loadable(
  lazy(() => import("../pages/dashboard/signatures/NewDocument"))
);
const SignaturesDocumentDetail = Loadable(
  lazy(() => import("../pages/dashboard/signatures/DocumentDetail"))
);
const SignaturesTemplatesList = Loadable(
  lazy(() => import("../pages/dashboard/signatures/TemplatesList"))
);
const SignaturesMarketplace = Loadable(
  lazy(() => import("../pages/dashboard/signatures/Marketplace"))
);
const SignaturesTemplateEditor = Loadable(
  lazy(() => import("../pages/dashboard/signatures/TemplateEditor"))
);
// Main
const SettingPage = Loadable(lazy(() => import("../pages/dashboard/SettingPage")));
const CalendarPage = Loadable(lazy(() => import("../pages/dashboard/CalendarPage")));
const InDevelopmentPage = Loadable(lazy(() => import("../pages/InDevelopmentPage")));
const Maintenance = Loadable(lazy(() => import("../pages/Maintenance")));
const Page500 = Loadable(lazy(() => import("../pages/Page500")));
const NotFound = Loadable(lazy(() => import("../pages/Page404")));
const StandaloneSignPage = Loadable(lazy(() => import("../pages/SignPage")));
const StandaloneDocumentViewer = Loadable(lazy(() => import("../pages/DocumentViewerPage")));
